import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  validateDancerCsv,
  reconcileDancerImport,
  type ImportSummary,
  DANCER_REQUIRED_HEADERS,
  type DancerInput,
} from '@pirouette/shared';
import { fetchFamilyIdsByEmail, useCommitDancerImport, useCreateImportBatch } from '@/data/imports';
import { Button } from '@/components/ui/Button';
import { Empty } from '@/components/ui/Empty';
import { ImportSummaryView } from '@/components/import/ImportSummaryView';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/dancers/import')({
  component: DancerImportWizard,
});

type Step = 'upload' | 'preview' | 'committing' | 'done';

interface PreviewState {
  filename: string;
  rows: DancerInput[];
  summary: ImportSummary;
}

function DancerImportWizard() {
  const navigate = useNavigate();
  const createBatch = useCreateImportBatch();
  const commit = useCommitDancerImport();
  const [step, setStep] = useState<Step>('upload');
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  async function handleFile(file: File) {
    setErrorText(null);
    try {
      const text = await file.text();
      const validation = validateDancerCsv(text);

      const emails = validation.pending.map((p) => p.familyEmail);
      const lookup = await fetchFamilyIdsByEmail(emails);
      const reconciled = reconcileDancerImport(validation, lookup);

      setPreview({
        filename: file.name,
        rows: reconciled.rows,
        summary: reconciled.summary,
      });
      setStep('preview');
    } catch (err) {
      setErrorText(err instanceof Error ? err.message : 'Could not read the file.');
    }
  }

  async function handleCommit() {
    if (!preview) return;
    setStep('committing');
    try {
      const batch = await createBatch.mutateAsync({
        kind: 'dancers',
        filename: preview.filename,
        rowCount: preview.summary.totalRows,
      });
      const result = await commit.mutateAsync({
        batchId: batch.id,
        rows: preview.rows,
      });
      toast({
        title:
          result.failed === 0
            ? 'Import complete'
            : result.committed === 0
              ? 'Import failed'
              : 'Import partially complete',
        description:
          result.failed === 0
            ? `${result.committed} dancers added.`
            : `${result.committed} added, ${result.failed} failed. Check the import batch for details.`,
        variant: result.failed === 0 ? 'success' : 'error',
      });
      setStep('done');
    } catch (err) {
      setErrorText(err instanceof Error ? err.message : 'Import failed.');
      setStep('preview');
    }
  }

  return (
    <div className="max-w-4xl space-y-8">
      <header>
        <Link
          to="/admin/dancers"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Dancer roster
        </Link>
        <p className="mt-2 tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Bulk import
        </p>
        <h1 className="mt-2">Import dancers from CSV</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Each row needs the parent's email so we can attach the dancer to the right family. Import
          families first if they're not already in the system.
        </p>
      </header>

      {step === 'upload' && (
        <section aria-labelledby="dancer-upload-heading" className="surface-elevated space-y-5 p-6">
          <h2 id="dancer-upload-heading" className="font-medium text-[var(--text-lg)]">
            Upload your CSV
          </h2>
          <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            Required columns: {DANCER_REQUIRED_HEADERS.join(', ')}.
          </p>
          <label className="block">
            <span className="sr-only">CSV file</span>
            <input
              type="file"
              accept=".csv,text/csv"
              className="block w-full text-[var(--text-sm)] file:mr-4 file:rounded-[var(--radius-md)] file:border-0 file:bg-[var(--color-accent)] file:px-4 file:py-2 file:font-medium file:text-[var(--color-ink-on-accent)] hover:file:bg-[var(--color-accent-hover)]"
              onChange={(e) => {
                const file = e.currentTarget.files?.[0];
                if (file) void handleFile(file);
              }}
            />
          </label>
          {errorText && (
            <p role="alert" className="text-[var(--color-danger)] text-[var(--text-sm)]">
              {errorText}
            </p>
          )}
        </section>
      )}

      {step === 'preview' && preview && (
        <section aria-labelledby="dancer-preview-heading" className="space-y-5">
          <h2 id="dancer-preview-heading" className="font-medium text-[var(--text-lg)]">
            Preview · {preview.filename}
          </h2>
          <ImportSummaryView summary={preview.summary} />
          {errorText && (
            <p role="alert" className="text-[var(--color-danger)] text-[var(--text-sm)]">
              {errorText}
            </p>
          )}
          <div className="flex flex-wrap gap-3">
            <Button disabled={preview.rows.length === 0} onClick={() => void handleCommit()}>
              Commit {preview.rows.length} {preview.rows.length === 1 ? 'row' : 'rows'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setPreview(null);
                setStep('upload');
              }}
            >
              Upload a different file
            </Button>
          </div>
        </section>
      )}

      {step === 'committing' && (
        <p aria-live="polite" className="text-[var(--color-ink-muted)]">
          Saving rows…
        </p>
      )}

      {step === 'done' && (
        <Empty
          title="Dancers added"
          description="Your dancers are now in the roster and visible to their parents."
          action={
            <Button onClick={() => void navigate({ to: '/admin/dancers' })}>
              View dancer roster
            </Button>
          }
        />
      )}
    </div>
  );
}
